import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FileCheck, 
  Upload, 
  Clock, 
  AlertTriangle,
  CheckCircle2,
  Car,
  Calendar,
  Users,
  Shield,
  CreditCard,
  FileText,
  MapPin,
  Phone,
  Heart
} from 'lucide-react';
import { PortalLayout } from './PortalLayout';
import { CredentialUpload } from './CredentialUpload';
import { CredentialsList } from './CredentialsList';
import { useExchangeAuth, exchangeApi } from '../../contexts/ExchangeAuthContext';
import { toast } from 'sonner';

// Patient-specific credential types
const PATIENT_CREDENTIALS = [
  { id: 'insurance_card', name: 'Insurance Card', icon: CreditCard, description: 'Health insurance card (front and back)' },
  { id: 'medicaid_card', name: 'Medicaid Card', icon: CreditCard, description: 'State Medicaid benefits card' },
  { id: 'medicare_card', name: 'Medicare Card', icon: CreditCard, description: 'Medicare benefits card' },
  { id: 'medical_authorization', name: 'Medical Authorization', icon: FileText, description: 'Transportation authorization form' },
  { id: 'pas_form', name: 'Prior Authorization (PAS)', icon: FileCheck, description: 'Prior Authorization Service form' },
  { id: 'doctors_orders', name: "Doctor's Orders", icon: Heart, description: 'Standing orders for transport' },
  { id: 'patient_id', name: 'Photo ID', icon: Shield, description: 'Government-issued ID' },
  { id: 'discharge_papers', name: 'Discharge Papers', icon: FileText, description: 'Hospital discharge documentation' }
];

export const PatientDashboard = () => {
  const { user } = useExchangeAuth();
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    expiring: 0,
    sharedWith: 0
  });

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const res = await exchangeApi.getCredentials();
      const creds = res.data.credentials || [];
      setCredentials(creds);
      
      // Calculate stats
      setStats({
        total: creds.length,
        verified: creds.filter(c => c.status === 'verified').length,
        expiring: creds.filter(c => ['expiring_soon', 'expired'].includes(c.status)).length,
        sharedWith: new Set(creds.flatMap(c => c.shared_with?.map(s => s.org_id) || [])).size
      });
    } catch (error) {
      console.error('Failed to load credentials:', error);
      toast.error('Failed to load your documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadSuccess = () => {
    loadCredentials();
    setShowUpload(false);
    toast.success('Document uploaded successfully!');
  };

  return (
    <PortalLayout role="patient">
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold text-white">
              Welcome, {user?.first_name || 'Patient'}
            </h1>
            <p className="text-slate-400">
              Manage your medical transportation documents in one place
            </p>
          </div>
          <button
            onClick={() => setShowUpload(!showUpload)}
            className="btn-primary"
            data-testid="upload-document-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {showUpload ? 'Cancel' : 'Upload Document'}
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-white">{stats.total}</p>
                <p className="text-sm text-slate-400">Total Documents</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-neon-cyan/20 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-neon-cyan" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-neon-green">{stats.verified}</p>
                <p className="text-sm text-slate-400">Verified</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-neon-green/20 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-neon-green" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-2xl font-bold ${stats.expiring > 0 ? 'text-yellow-400' : 'text-slate-400'}`}>
                  {stats.expiring}
                </p>
                <p className="text-sm text-slate-400">Expiring Soon</p>
              </div>
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stats.expiring > 0 ? 'bg-yellow-500/20' : 'bg-white/5'}`}>
                <AlertTriangle className={`w-5 h-5 ${stats.expiring > 0 ? 'text-yellow-400' : 'text-slate-500'}`} />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-neon-purple">{stats.sharedWith}</p>
                <p className="text-sm text-slate-400">Providers Shared</p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-neon-purple/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-neon-purple" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Upload Section */}
        {showUpload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <CredentialUpload 
              role="patient" 
              onSuccess={handleUploadSuccess}
              onClose={() => setShowUpload(false)}
            />
          </motion.div>
        )}

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Required Documents Checklist */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-6"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FileCheck className="w-5 h-5 mr-2 text-neon-cyan" />
              Document Checklist
            </h3>
            <div className="space-y-3">
              {PATIENT_CREDENTIALS.slice(0, 5).map((cred) => {
                const Icon = cred.icon;
                const hasDoc = credentials.some(c => c.credential_type === cred.id);
                return (
                  <div 
                    key={cred.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${hasDoc ? 'bg-neon-green/10 border border-neon-green/30' : 'bg-white/5'}`}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-4 h-4 ${hasDoc ? 'text-neon-green' : 'text-slate-500'}`} />
                      <span className={hasDoc ? 'text-white' : 'text-slate-400'}>{cred.name}</span>
                    </div>
                    {hasDoc ? (
                      <CheckCircle2 className="w-5 h-5 text-neon-green" />
                    ) : (
                      <button
                        onClick={() => setShowUpload(true)}
                        className="text-xs text-neon-cyan hover:underline"
                      >
                        Upload
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Book a Ride CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-6 border-neon-cyan/30"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Car className="w-5 h-5 mr-2 text-neon-cyan" />
              Need a Ride?
            </h3>
            <p className="text-slate-400 text-sm mb-4">
              Book NEMT transportation for your medical appointments. Your uploaded documents make booking faster.
            </p>
            <div className="space-y-3">
              <a 
                href="https://ovadispatch.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-primary w-full justify-center"
              >
                <MapPin className="w-4 h-4 mr-2" />
                Book a Ride on OVA Dispatch
              </a>
              <a href="tel:1-800-555-1234" className="btn-secondary w-full justify-center">
                <Phone className="w-4 h-4 mr-2" />
                Call to Schedule: 1-800-555-1234
              </a>
            </div>
          </motion.div>
        </div>

        {/* My Documents */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="glass-card p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FileText className="w-5 h-5 mr-2 text-neon-cyan" />
              My Documents
            </h3>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-neon-cyan border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <CredentialsList 
              credentials={credentials} 
              onRefresh={loadCredentials}
              role="patient"
            />
          )}
        </motion.div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="glass-card p-6 border-neon-green/20"
          >
            <h4 className="text-white font-semibold mb-3 flex items-center">
              <Shield className="w-5 h-5 mr-2 text-neon-green" />
              Your Data is Secure
            </h4>
            <p className="text-sm text-slate-400">
              All documents are encrypted with AES-256 encryption and verified on blockchain. 
              You control who can access your information.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="glass-card p-6 border-neon-purple/20"
          >
            <h4 className="text-white font-semibold mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2 text-neon-purple" />
              Share with Providers
            </h4>
            <p className="text-sm text-slate-400">
              Securely share your insurance and authorization documents with transportation providers. 
              No more faxing or repeated uploads.
            </p>
          </motion.div>
        </div>
      </div>
    </PortalLayout>
  );
};

export default PatientDashboard;
